import { useState, useEffect, useCallback, useRef } from 'react'

function throttle(fn, limit) {
  let lastCall = 0
  return function (...args) {
    const now = Date.now()
    if (now - lastCall >= limit) {
      lastCall = now
      fn(...args)
    }
  }
}

export default function useInactivityTimeout({ enabled, onTimeout, warningMs, timeoutMs }) {
  const [showWarning, setShowWarning] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(0)

  const warningRef = useRef(null)
  const timeoutRef = useRef(null)
  const countdownRef = useRef(null)
  const onTimeoutRef = useRef(onTimeout)

  // Keep callback ref current without triggering effect re-runs
  useEffect(() => {
    onTimeoutRef.current = onTimeout
  }, [onTimeout])

  const clearAllTimers = useCallback(() => {
    if (warningRef.current) clearTimeout(warningRef.current)
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (countdownRef.current) clearInterval(countdownRef.current)
    warningRef.current = null
    timeoutRef.current = null
    countdownRef.current = null
  }, [])

  const resetTimer = useCallback(() => {
    clearAllTimers()
    setShowWarning(false)

    if (!enabled) return

    const warningDelay = warningMs
    const timeoutDelay = timeoutMs
    const countdownDuration = timeoutDelay - warningDelay

    warningRef.current = setTimeout(() => {
      setShowWarning(true)
      setTimeRemaining(countdownDuration)

      countdownRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          const next = prev - 1000
          if (next <= 0) {
            clearInterval(countdownRef.current)
            countdownRef.current = null
            return 0
          }
          return next
        })
      }, 1000)
    }, warningDelay)

    timeoutRef.current = setTimeout(() => {
      clearAllTimers()
      setShowWarning(false)
      onTimeoutRef.current?.()
    }, timeoutDelay)
  }, [enabled, warningMs, timeoutMs, clearAllTimers])

  useEffect(() => {
    if (!enabled) {
      clearAllTimers()
      setShowWarning(false)
      return
    }

    const handleActivity = throttle(() => {
      resetTimer()
    }, 1000)

    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart']
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true })
    })

    // Start initial timer
    resetTimer()

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })
      clearAllTimers()
    }
  }, [enabled, resetTimer, clearAllTimers])

  return { showWarning, timeRemaining, resetTimer }
}
